import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { CardTitle, Text } from "@/components/ui/text";
import { HStack } from "@/components/ui/h-stack";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { SettingsPageLayout } from "@/components/ui/settings-page-layout";
import { SectionGrid } from "@/components/ui/section-grid";
import { VStack } from "@/components/ui/v-stack";
import { Icon } from "@/components/ui/icons";
import { useTheme } from "@/contexts/theme-context";
import {
  useWorkspaceMode,
  type WorkspaceMode,
} from "@/contexts/workspace-mode-context";
import {
  ALL_LANGUAGES,
  fromIntlLanguage,
  languageFlags,
  languageNames,
  setLanguage as persistLanguage,
  type AppLanguage,
} from "@/lib/i18n/config";

function PreferencesPage() {
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tCommon } = useTranslation("common");
  const { t: tSettings } = useTranslation("settings");
  const { theme, setTheme } = useTheme();
  const { mode, override, setModeOverride } = useWorkspaceMode();

  const handleLanguageChange = (lang: AppLanguage) => {
    if (lang === language) {
      return;
    }

    persistLanguage(lang);
  };

  const handleThemeChange = (newTheme: "light" | "dark" | "system") => {
    setTheme(newTheme);
  };

  const workspaceModeOptions: {
    value: WorkspaceMode | "auto";
    label: string;
    icon: typeof Icon.User;
    iconColor: string;
  }[] = [
    {
      value: "auto",
      label: tSettings("workspaceModeAuto"),
      icon: Icon.ArrowPath,
      iconColor: "text-[color:var(--on-surface-variant)]",
    },
    {
      value: "solo",
      label: tSettings("workspaceModeSolo"),
      icon: Icon.User,
      iconColor: "text-[color:var(--accent-blue)]",
    },
    {
      value: "team",
      label: tSettings("workspaceModeTeam"),
      icon: Icon.Building,
      iconColor: "text-[color:var(--accent-green)]",
    },
    {
      value: "full",
      label: tSettings("workspaceModeFull"),
      icon: Icon.Grid,
      iconColor: "text-[color:var(--accent-purple)]",
    },
  ];

  const themeOptions = [
    {
      value: "light" as const,
      label: tSettings("lightMode"),
      icon: Icon.Sun,
      iconColor: "text-[color:var(--accent-yellow)]",
    },
    {
      value: "dark" as const,
      label: tSettings("darkMode"),
      icon: Icon.Moon,
      iconColor: "text-[color:var(--accent-purple)]",
    },
    {
      value: "system" as const,
      label: tSettings("systemMode"),
      icon: Icon.Computer,
      iconColor: "text-[color:var(--on-surface-variant)]",
    },
  ];

  return (
    <VStack gap="xl" data-testid="preferences-page">
      <PageNavigationHeader
        title={tSettings("preferences")}
        contextLabel={tSettings("account")}
        metadata={tSettings("preferencesDescription")}
        onBack={() => void navigate({ to: "/account" })}
        backAriaLabel={tCommon("back")}
        contextAriaLabel={tSettings("account")}
      />

      <SettingsPageLayout variant="grid-2">
        <SectionGrid cols="lg" gap="md" data-testid="preferences-cards">
          {/* Language Settings */}
          <Card padding="none" data-testid="language-card">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">{tSettings("language")}</CardTitle>
            </CardHeader>
            <VStack className="px-4 pb-4">
              <VStack gap="xs">
                {ALL_LANGUAGES.map((lang) => (
                  <Button
                    type="button"
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    variant="ghost"
                    size="sm"
                    className="w-full !flex !items-center !justify-between text-left px-3 py-2 rounded-md"
                    data-testid={`language-option-${lang}`}
                  >
                    <HStack>
                      <Text size="base">{languageFlags[lang]}</Text>
                      <Text weight="medium">{languageNames[lang]}</Text>
                    </HStack>
                    {language === lang && (
                      <Icon.Check size="sm" color="success" />
                    )}
                  </Button>
                ))}
              </VStack>
              <Text size="xs" color="muted" className="mt-3">
                {tSettings("languageDescription")}
              </Text>
            </VStack>
          </Card>

          {/* Theme Settings */}
          <Card padding="none" data-testid="theme-card">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">{tSettings("theme")}</CardTitle>
            </CardHeader>
            <VStack className="px-4 pb-4">
              <VStack gap="xs">
                {themeOptions.map((option) => {
                  const ThemeIcon = option.icon;
                  return (
                    <Button
                      type="button"
                      key={option.value}
                      onClick={() => handleThemeChange(option.value)}
                      variant="ghost"
                      size="sm"
                      className="w-full !flex !items-center !justify-between text-left px-3 py-2 rounded-md"
                      data-testid={`theme-${option.value}`}
                    >
                      <HStack>
                        <ThemeIcon size="sm" className={option.iconColor} />
                        <Text weight="medium">{option.label}</Text>
                      </HStack>
                      {theme === option.value && (
                        <Icon.Check size="sm" color="success" />
                      )}
                    </Button>
                  );
                })}
              </VStack>
              <Text size="xs" color="muted" className="mt-3">
                {tSettings("themeDescription")}
              </Text>
            </VStack>
          </Card>

          {/* Workspace Mode Settings */}
          <Card padding="none" data-testid="workspace-mode-card">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">
                {tSettings("workspaceMode")}
              </CardTitle>
            </CardHeader>
            <VStack className="px-4 pb-4">
              <VStack gap="xs">
                {workspaceModeOptions.map((option) => {
                  const ModeIcon = option.icon;
                  const isSelected = override === option.value;
                  return (
                    <Button
                      type="button"
                      key={option.value}
                      onClick={() => setModeOverride(option.value)}
                      variant="ghost"
                      size="sm"
                      className="w-full !flex !items-center !justify-between text-left px-3 py-2 rounded-md"
                      data-testid={`workspace-mode-${option.value}`}
                    >
                      <HStack>
                        <ModeIcon size="sm" className={option.iconColor} />
                        <Text weight="medium">{option.label}</Text>
                        {option.value === "auto" && (
                          <Text size="xs" color="muted">
                            ({mode})
                          </Text>
                        )}
                      </HStack>
                      {isSelected && <Icon.Check size="sm" color="success" />}
                    </Button>
                  );
                })}
              </VStack>
              <Text size="xs" color="muted" className="mt-3">
                {tSettings("workspaceModeDescription")}
              </Text>
            </VStack>
          </Card>
        </SectionGrid>
      </SettingsPageLayout>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/account/preferences")({
  component: PreferencesPage,
});
